import React, { createContext, useContext, useEffect, useState, useRef, ReactNode, useMemo, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionQuery } from '@/hooks/queries/useSessionQuery';
import { useUserRolesQuery, UserRole } from '@/hooks/queries/useUserRolesQuery';
import { logger } from '@/lib/logger';
import { errorTracker } from '@/lib/error-tracking';
import { getPrimaryRole, USER_ROLES } from '@/config/roles';
import { setUserProperties, clearUser } from '@/lib/analytics';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  roles: UserRole[];
  currentRole: UserRole | null;
  loading: boolean;
  rolesError: boolean;
  hasRole: (role: UserRole) => boolean;
  switchRole: (role: UserRole) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [currentRole, setCurrentRole] = useState<UserRole | null>(null);
  const listenerSetupRef = useRef(false);

  // Use centralized React Query hooks
  const { data: session, isLoading: sessionLoading, isFetching: sessionFetching } = useSessionQuery();
  const user = session?.user || null;
  const userId = user?.id || null;

  // Fetch user roles from database
  const { data: rolesData, isLoading: rolesLoading, isFetching: rolesFetching, isError: rolesError } = useUserRolesQuery(userId);
  const roles = useMemo(() => rolesData?.roles || [], [rolesData?.roles]);

  // IMPROVED LOADING STATE: Handle race conditions better
  // Show loading when:
  // 1. Session is initially loading (no cached data)
  // 2. User exists but roles are still loading OR fetching (race condition after sign-in)
  // 3. CRITICAL FIX: Include rolesFetching alone - during refetch, isLoading might be false
  //    (because there's cached data) but isFetching is true. We must wait for roles to complete.
  // 4. When roles query fails (isError), stop loading - treat as empty roles so user can recover
  const isInitialSessionLoad = sessionLoading && !session;
  const isRolesLoadForUser = !!userId && rolesLoading && !rolesError;
  const isRolesFetchingForUser = !!userId && rolesFetching && !rolesError;

  const loading = isInitialSessionLoad || isRolesLoadForUser || isRolesFetchingForUser;

  // SINGLE auth state listener - prevents race conditions and duplicate subscriptions
  // FIXED: Use ref to track subscription and prevent duplicates
  useEffect(() => {
    // Guard: Only set up listener once per component lifecycle
    if (listenerSetupRef.current) {
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Only log significant auth events to reduce console noise
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        logger.info('Auth state changed', { event, userId: session?.user?.id });
      }

      // Handle invalid refresh token errors - clear stale tokens silently
      // This prevents the error from blocking the app
      if (event === 'TOKEN_REFRESHED' && !session) {
        // Token refresh failed - likely invalid refresh token
        // This is non-critical - the user can still use the app with their current session
        logger.warn('Token refresh failed - invalid refresh token (non-critical)', { 
          note: 'User can continue with current session' 
        });
        // Don't sign out - let the user continue with their current session
        // The refresh token error is handled by Supabase and won't block functionality
        return;
      }
      
      // Handle other auth errors that might indicate session issues
      if (event === 'USER_UPDATED' && !session) {
        logger.warn('User updated but no session - may need to re-authenticate');
      }

      // Invalidate queries on auth changes FIRST - this will trigger role updates
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        queryClient.invalidateQueries({ queryKey: ['session'] });
        queryClient.invalidateQueries({ queryKey: ['user_roles'] });
      }

      // Handle different auth events
      if (event === 'SIGNED_IN' && session?.user) {
        // Validate session integrity
        const now = Date.now();
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;

        if (expiresAt && expiresAt <= now) {
          logger.warn('Signed in with expired session', { expiresAt: new Date(expiresAt) });
        }

        // Update Sentry user context after a brief delay to allow roles to update
        setTimeout(() => {
          // Get fresh roles data from query cache instead of stale closure
          const rolesQueryData = queryClient.getQueryData<{ roles: UserRole[] }>(['user_roles', session.user.id]);
          const currentRoles = rolesQueryData?.roles || [];
          errorTracker.setUser(session.user.id, session.user.email, {
            roles: currentRoles.join(','),
          });
          // Set user ID in Google Analytics for accurate tracking
          setUserProperties(session.user.id, {
            roles: currentRoles.join(','),
            email: session.user.email,
          });
        }, 100);
      } else if (event === 'SIGNED_OUT') {
        errorTracker.clearUser();
        clearUser(); // Clear GA user ID
        setCurrentRole(null);

        // Clear any cached queries that might contain sensitive data
        queryClient.clear();
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        logger.info('Token refreshed successfully', { userId: session.user.id });
      }
    });

    listenerSetupRef.current = true;

    return () => {
      subscription.unsubscribe();
      listenerSetupRef.current = false; // Reset on unmount so it can be set up again if component remounts
    };
  }, [queryClient]); // FIXED: Removed rolesData to prevent dependency loop

  // Determine current role based on database roles ONLY
  // No localStorage for authorization - only for UX preference
  // Uses getPrimaryRole() from roles.ts for consistent priority ordering
  // BUT: Respects manual role switches (especially for admin acting as buyer/supplier)
  useEffect(() => {
    // Only set role when we have both user and roles
    if (!userId || !user || roles.length === 0) {
      setCurrentRole(null);
      return;
    }

    const isAdmin = roles.includes(USER_ROLES.ADMIN);
    
    // Check if user has manually switched to a role (stored in localStorage)
    // This is important for admin users who want to act as buyer/supplier
    const storedRole = typeof window !== 'undefined' 
      ? localStorage.getItem('holescale_active_role') as UserRole | null
      : null;
    
    // If admin has manually switched to buyer/supplier, respect that choice
    // This must happen BEFORE getPrimaryRole to prevent override
    if (isAdmin && storedRole && (storedRole === 'buyer' || storedRole === 'supplier')) {
      setCurrentRole(prevRole => {
        if (prevRole === storedRole) return prevRole;
        queueMicrotask(() => logger.info('Admin acting as role (from localStorage)', { storedRole, userId, prevRole }));
        return storedRole;
      });
      // Update Sentry with acting role
      errorTracker.setUser(userId, user.email, {
        roles: roles.join(','),
        currentRole: storedRole,
        actingAs: storedRole,
      });
      return; // Don't override with getPrimaryRole
    }
    
    // If there's a stored role that matches a valid role, use it (for non-admin users)
    if (storedRole && roles.includes(storedRole) && storedRole !== 'admin') {
      setCurrentRole(prevRole => {
        if (prevRole === storedRole) return prevRole;
        queueMicrotask(() => logger.info('Using stored role preference', { storedRole, userId }));
        return storedRole;
      });
      errorTracker.setUser(userId, user.email, {
        roles: roles.join(','),
        currentRole: storedRole,
      });
      return; // Don't override with getPrimaryRole
    }

    // Use getPrimaryRole() helper for consistent priority: Admin > Supplier > Buyer > Support
    // This enforces server-side authorization
    const newRole = getPrimaryRole(roles);

    // Only update if role actually changed to prevent unnecessary re-renders
    // Defer log to avoid "Cannot update component while rendering another" (debugger listens to logger)
    setCurrentRole(prevRole => {
      if (prevRole === newRole) return prevRole;
      queueMicrotask(() => logger.info('Role determined', { userId, roles: roles.join(','), newRole }));
      return newRole;
    });

    // Update Sentry user context with roles
    errorTracker.setUser(userId, user.email, {
      roles: roles.join(','),
      currentRole: newRole,
    });
  }, [userId, user, roles]); // FIXED: Added user to deps to ensure user context is available

  // PERFORMANCE: Memoize hasRole to prevent unnecessary re-renders
  const hasRole = useCallback((role: UserRole): boolean => {
    return roles.includes(role);
  }, [roles]);

  // PERFORMANCE: Memoize switchRole to prevent unnecessary re-renders
  const switchRole = useCallback((newRole: UserRole) => {
    const isAdmin = roles.includes(USER_ROLES.ADMIN);
    const hasRequestedRole = roles.includes(newRole);
    
    // Allow admins to switch to buyer/supplier for UI testing, even without those roles in DB
    if (isAdmin && (newRole === 'buyer' || newRole === 'supplier')) {
      // Store in localStorage so it persists across page reloads
      if (typeof window !== 'undefined') {
        localStorage.setItem('holescale_active_role', newRole);
      }
      setCurrentRole(newRole);
      logger.info('Admin switched to acting role', { newRole, userId, note: 'Admin acting as user role for testing' });
      return;
    }
    
    // Verify user actually has this role in database (for non-admins or admin switching to admin)
    if (!hasRequestedRole) {
      logger.error('Attempted to switch to unauthorized role', {
        requestedRole: newRole,
        availableRoles: roles
      });
      return;
    }

    // Store in localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('holescale_active_role', newRole);
    }
    
    // Switch role - this is just UX preference
    setCurrentRole(newRole);
    logger.info('Role switched', { newRole, userId });
  }, [roles, userId]);

  // PERFORMANCE: Memoize context value to prevent unnecessary provider re-renders
  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    roles,
    currentRole,
    loading,
    rolesError: !!rolesError,
    hasRole,
    switchRole,
  }), [user, session, roles, currentRole, loading, rolesError, hasRole, switchRole]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

/** Safe version that returns undefined when outside AuthProvider (e.g. during route transitions) */
export function useOptionalAuthContext() {
  return useContext(AuthContext);
}
